import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { Language as LanguageIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginCredentials } from '../types';

const Login: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState<LoginCredentials>({
    username: '',
    password: ''
  });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const handleChange = (field: keyof LoginCredentials) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCredentials(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(credentials);
      // التوجيه إلى لوحة التحكم بعد تسجيل الدخول بنجاح
      navigate('/dashboard');
    } catch (err: any) {
      setError(t('loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (event: any) => {
    const newLang = event.target.value;
    i18n.changeLanguage(newLang);
    document.dir = newLang === 'ar' ? 'rtl' : 'ltr';
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        direction: i18n.language === 'ar' ? 'rtl' : 'ltr'
      }}
    >
      <Container maxWidth="sm">
        <Card
          sx={{
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            borderRadius: 3,
            overflow: 'visible',
            direction: i18n.language === 'ar' ? 'rtl' : 'ltr'
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Language Selector */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: i18n.language === 'ar' ? 'flex-start' : 'flex-end', 
              mb: 2 
            }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>{t('language')}</InputLabel>
                <Select
                  value={i18n.language}
                  onChange={handleLanguageChange}
                  label={t('language')}
                >
                  <MenuItem value="ar">{t('arabic')}</MenuItem>
                  <MenuItem value="en">{t('english')}</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Logo and Title */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  fontSize: '2rem'
                }}
              >
                🌱
              </Box>
              <Typography variant="h4" component="h1" gutterBottom>
                {t('login')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('welcomeMessage')}
              </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3, textAlign: i18n.language === 'ar' ? 'right' : 'left' }}>
                {error}
              </Alert>
            )}

            {/* Login Form */}
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label={t('username')}
                value={credentials.username}
                onChange={handleChange('username')}
                margin="normal"
                required
                autoFocus
                disabled={loading}
                sx={{ 
                  mb: 2,
                  '& .MuiInputLabel-root': {
                    transformOrigin: i18n.language === 'ar' ? 'top right' : 'top left',
                    right: i18n.language === 'ar' ? 14 : 'auto',
                    left: i18n.language === 'ar' ? 'auto' : 14
                  },
                  '& .MuiOutlinedInput-root': {
                    textAlign: i18n.language === 'ar' ? 'right' : 'left'
                  }
                }}
              />
              
              <TextField
                fullWidth
                label={t('password')}
                type="password"
                value={credentials.password}
                onChange={handleChange('password')}
                margin="normal"
                required
                disabled={loading}
                sx={{ 
                  mb: 3,
                  '& .MuiInputLabel-root': {
                    transformOrigin: i18n.language === 'ar' ? 'top right' : 'top left',
                    right: i18n.language === 'ar' ? 14 : 'auto',
                    left: i18n.language === 'ar' ? 'auto' : 14
                  },
                  '& .MuiOutlinedInput-root': {
                    textAlign: i18n.language === 'ar' ? 'right' : 'left'
                  }
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !credentials.username || !credentials.password}
                sx={{
                  py: 1.5,
                  background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #45a049 0%, #1B5E20 100%)',
                  }
                }}
              >
                {loading ? t('loading') : t('loginButton')}
              </Button>
            </Box>

            {/* Demo Credentials */}
            <Box sx={{ 
              mt: 3, 
              p: 2, 
              bgcolor: 'grey.50', 
              borderRadius: 1,
              textAlign: i18n.language === 'ar' ? 'right' : 'left'
            }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {i18n.language === 'ar' ? 'بيانات تجريبية:' : 'Demo Credentials:'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {i18n.language === 'ar' ? 'اسم المستخدم: admin' : 'Username: admin'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {i18n.language === 'ar' ? 'كلمة المرور: admin123' : 'Password: admin123'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Login;
